import { useCallback, useEffect, useRef, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Copy, Check, Download, Image, QrCode, Share2 } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import { apiClient } from '../App';
import { getParticipantCount } from '../utils/participantCount';
import { getUserTimezone } from '../utils/datetime';
import { deriveRunLifecycle, RUN_LIFECYCLE } from '../utils/runLifecycle';
import InstagramShareSheet from './InstagramShareSheet';

/**
 * ShareRunSheet - Bottom sheet for sharing a run
 * 
 * Options:
 * A) Share - Opens native share sheet
 * B) Copy Invite - Copies message + link to clipboard
 * 
 * Message format (natural, no branding):
 * "running at [court] at [time]. need [x] more. grab a spot: [link]"
 */
const ShareRunSheet = ({ 
  open, 
  onOpenChange, 
  run, 
  source = 'game_detail' // 'game_detail', 'post_join', 'host_banner'
}) => {
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [instagramOpen, setInstagramOpen] = useState(false);
  const [instagramPreview, setInstagramPreview] = useState(null);
  const [instagramLoading, setInstagramLoading] = useState(false);
  const [clickableShareBusy, setClickableShareBusy] = useState(false);
  const [qrVisible, setQrVisible] = useState(false);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrShareUrl, setQrShareUrl] = useState('');
  const qrCodeContainerRef = useRef(null);
  const activeRunIdRef = useRef(null);
  const runData = run || {};
  const runId = runData.id;
  activeRunIdRef.current = runId;

  const lifecycle = deriveRunLifecycle(runData);
  const shareUnavailable = lifecycle !== RUN_LIFECYCLE.ACTIVE;
  const isPrivate = runData.is_private === true || runData.privacy === 'private';

  const trackEvent = useCallback(async (eventType) => {
    if (!runId) return;
    try {
      await apiClient.post('/share/events', {
        event_type: eventType,
        run_id: runId,
        source,
      });
    } catch {
      // Analytics must never block sharing.
    }
  }, [runId, source]);

  useEffect(() => {
    setInstagramPreview(null);
    setCopied(false);
    setLinkCopied(false);
    setInstagramOpen(false);
    setQrVisible(false);
    setQrShareUrl('');
  }, [runId]);

  useEffect(() => {
    if (open && runId && !shareUnavailable) {
      trackEvent('share_sheet_opened');
    }
  }, [open, runId, shareUnavailable, trackEvent]);

  // Format time: "7pm" - uses user's local timezone, no TZ label
  const formatTimeShort = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      const userTimezone = getUserTimezone();
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        hour12: true,
        timeZone: userTimezone,
      }).toLowerCase().replace(' ', '');
    } catch {
      return '';
    }
  };
  
  // Calculate spots left
  const rosterPlayerCount = getParticipantCount(runData);
  const suppliedPlayerCount = Number(runData.player_count);
  const playerCount = runData.participant_count ?? (runData.player_count !== null
    && runData.player_count !== undefined
    && Number.isFinite(suppliedPlayerCount)
    && suppliedPlayerCount >= 0
    ? suppliedPlayerCount
    : rosterPlayerCount);
  const maxPlayers = runData.max_players || 10;
  const suppliedSpotsRemaining = Number(runData.spots_remaining);
  const spotsLeft = runData.spots_remaining !== null
    && runData.spots_remaining !== undefined
    && Number.isFinite(suppliedSpotsRemaining)
    && suppliedSpotsRemaining >= 0
    ? suppliedSpotsRemaining
    : Math.max(0, maxPlayers - playerCount);
  
  // Build share link - production domain
  const fallbackShareLink = runId ? `https://openrunsusa.com/run/${runId}` : '';
  const publicShareMessage = String(runData.public_share_message || '').trim();
  // GameDetail renders description as host-authored run copy; use it only after explicit public share fields.
  const publicDescriptionMessage = String(runData.description || '').trim();

  const formatDateForShare = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      const userTimezone = getUserTimezone();
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        timeZone: userTimezone,
      });
    } catch {
      return '';
    }
  };

  const getPreviewPublicMessage = (preview) => {
    if (preview && Object.prototype.hasOwnProperty.call(preview, 'public_share_message')) {
      const previewMessage = String(preview.public_share_message || '').trim();
      if (previewMessage) return previewMessage;
    }
    return publicShareMessage || publicDescriptionMessage;
  };

  const formatAvailability = (spotsOpen, waitlistEnabled = runData.waitlist_enabled) => {
    if (spotsOpen > 0) {
      return `${spotsOpen} ${spotsOpen === 1 ? 'spot open' : 'spots open'}`;
    }
    return waitlistEnabled ? 'Waitlist open' : 'Currently full';
  };

  const buildShareMessage = (preview) => {
    const shareLink = preview?.share_url || fallbackShareLink;
    const previewMessage = getPreviewPublicMessage(preview);
    const title = String(preview?.public_title || preview?.title || runData.title || '').trim();
    const dateLabel = formatDateForShare(preview?.date_time || runData.date_time);
    const timeLabel = formatTimeShort(preview?.date_time || runData.date_time);
    const locationLabel = preview?.public_location_label
      || preview?.city
      || runData.public_location_label
      || runData.city
      || 'Location shared after joining';
    const previewSpotsLeft = preview?.spots_left ?? spotsLeft;
    const previewMaxPlayers = preview?.max_players || maxPlayers;
    const previewPlayerCount = preview?.player_count
      ?? Math.max(0, previewMaxPlayers - previewSpotsLeft);
    const availability = formatAvailability(previewSpotsLeft, preview?.waitlist_enabled ?? runData.waitlist_enabled);
    const lines = [];

    if (previewMessage) {
      lines.push(previewMessage, '');
    }

    if (title) lines.push(title);
    if (dateLabel || timeLabel) lines.push([dateLabel, timeLabel].filter(Boolean).join(' at '));
    if (locationLabel) lines.push(locationLabel);
    if (availability) {
      lines.push(`${previewPlayerCount} of ${previewMaxPlayers} joined · ${availability}`);
    }

    if (shareLink) {
      lines.push('', 'Join here:', shareLink);
    }

    return lines.join('\n');
  };

  const shareMessage = buildShareMessage(instagramPreview);

  const loadPublicPreview = async () => {
    if (!runId) return null;
    if (instagramPreview) return instagramPreview;
    const requestedRunId = runId;
    const response = await apiClient.get(`/runs/${runId}/preview`);
    if (activeRunIdRef.current !== requestedRunId) return null;
    setInstagramPreview(response.data);
    return response.data;
  };

  useEffect(() => {
    if (!open || !runId || instagramPreview || shareUnavailable) return;
    let cancelled = false;
    const requestedRunId = runId;

    const loadPreview = async () => {
      try {
        const response = await apiClient.get(`/runs/${runId}/preview`);
        if (!cancelled && activeRunIdRef.current === requestedRunId) {
          setInstagramPreview(response.data);
        }
      } catch {
        // Fall back to the authenticated run data already available.
      }
    };

    loadPreview();
    return () => {
      cancelled = true;
    };
  }, [open, runId, instagramPreview, shareUnavailable]);

  if (!run) return null;
  const sheetTitle = source === 'host_banner' ? 'Invite Players' : 'Share Run';

  const formatDateLong = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      const userTimezone = getUserTimezone();
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        timeZone: userTimezone,
      });
    } catch {
      return '';
    }
  };

  const getInstagramData = () => {
    const preview = instagramPreview || {};
    const publicType = preview.public_type === 'training' ? 'training' : 'run';
    const typeLabel = publicType === 'training' ? 'Training' : 'Open Run';
    const locationLabel = preview.public_location_label || preview.city || 'Location shared after joining';
    const rawTitle = String(preview.public_title || preview.title || '').trim();
    const genericTitles = new Set(['', 'pickup run', 'open run', 'run', 'training', 'basketball training']);
    const hasCustomTitle = rawTitle && !genericTitles.has(rawTitle.toLowerCase());
    const headline = hasCustomTitle
      ? rawTitle
      : (locationLabel === 'Location shared after joining' ? typeLabel : `${typeLabel} at ${locationLabel}`);
    const joinedCount = Math.max(0, (preview.max_players || maxPlayers) - (preview.spots_left ?? spotsLeft));
    const previewSpotsLeft = preview.spots_left ?? spotsLeft;
    const spotsLabel = formatAvailability(previewSpotsLeft, preview.waitlist_enabled ?? run.waitlist_enabled);

    return {
      headline,
      typeLabel,
      dateLabel: formatDateLong(preview.date_time || run.date_time),
      timeLabel: formatTimeShort(preview.date_time || run.date_time),
      locationLabel,
      capacityLabel: `${joinedCount} of ${preview.max_players || maxPlayers} joined`,
      spotsLabel,
      shareUrl: preview.share_url || fallbackShareLink,
      publicShareMessage: getPreviewPublicMessage(preview),
      publicType
    };
  };
  
  const handleInstagramShare = async () => {
    if (shareUnavailable) return;
    setInstagramLoading(true);
    try {
      const preview = await loadPublicPreview();
      if (!preview) return;
      setInstagramOpen(true);
      trackEvent('share_image_sheet_opened');
    } catch {
      toast.error('Could not load share preview');
    } finally {
      setInstagramLoading(false);
    }
  };
  
  // Handle copy to clipboard
  const handleCopy = async () => {
    if (shareUnavailable) return;
    let message = shareMessage;
    try {
      const preview = await loadPublicPreview();
      if (!preview) return;
      message = buildShareMessage(preview);
    } catch {
      // Fall back to the local run object if preview loading fails.
    }
    
    try {
      await navigator.clipboard.writeText(message);
      trackEvent('invite_text_copied');
      setCopied(true);
      toast.success('Invite text copied');
      
      // Reset copied state after 2s
      setTimeout(() => setCopied(false), 2000);
      
      // Close sheet after brief delay
      setTimeout(() => onOpenChange(false), 500);
    } catch (err) {
      toast.error("Couldn't copy automatically. Please copy manually.");
    }
  };

  const handleCopyLink = async () => {
    if (shareUnavailable) return;
    let link = fallbackShareLink;
    try {
      const preview = await loadPublicPreview();
      if (!preview) return;
      link = preview?.share_url || fallbackShareLink;
    } catch {
      // Fall back to the public run URL if preview loading fails.
    }

    try {
      await navigator.clipboard.writeText(link);
      trackEvent('join_link_copied');
      setLinkCopied(true);
      toast.success('Join link copied');
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy automatically. Please copy manually.");
    }
  };

  const handleSendClickableInvite = async () => {
    if (shareUnavailable) return;
    setClickableShareBusy(true);
    let preview = instagramPreview;
    try {
      preview = await loadPublicPreview();
      if (!preview) return;
    } catch {
      // Fall back to the authenticated run data already available.
    }

    const message = buildShareMessage(preview);
    const link = preview?.share_url || fallbackShareLink;

    try {
      if (navigator.share) {
        trackEvent('native_share_selected');
        await navigator.share({
          title: runData.title || 'OpenRuns invite',
          text: message,
          url: link || undefined,
        });
        trackEvent('clickable_invite_shared');
        return;
      }

      await navigator.clipboard.writeText(message);
      trackEvent('invite_text_copied');
      toast.success('Invite text copied');
    } catch (err) {
      if (err?.name !== 'AbortError') {
        toast.error("Couldn't share automatically. Copy the invite text instead.");
      }
    } finally {
      setClickableShareBusy(false);
    }
  };

  const handleShowQrCode = async () => {
    if (shareUnavailable) return;
    if (qrVisible) {
      setQrVisible(false);
      return;
    }

    setQrLoading(true);
    try {
      const preview = await loadPublicPreview();
      if (!preview || activeRunIdRef.current !== runId) return;
      const canonicalLink = preview.share_url || fallbackShareLink;
      if (!canonicalLink) throw new Error('Missing canonical link');
      setQrShareUrl(canonicalLink);
      setQrVisible(true);
      trackEvent('qr_code_viewed');
    } catch {
      toast.error('Could not load the QR code');
    } finally {
      setQrLoading(false);
    }
  };

  const handleDownloadQrCode = () => {
    const svg = qrCodeContainerRef.current?.querySelector('svg');
    if (!svg || !qrShareUrl || typeof URL.createObjectURL !== 'function') {
      toast.error('Could not save the QR code');
      return;
    }

    try {
      const svgText = new XMLSerializer().serializeToString(svg);
      const blob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `openruns-${runId}-qr.svg`;
      link.click();
      URL.revokeObjectURL(objectUrl);
      trackEvent('qr_code_downloaded');
    } catch {
      toast.error('Could not save the QR code');
    }
  };
  
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent 
        side="bottom" 
        className="rounded-t-2xl px-6 pb-8 pt-4"
        data-testid="share-run-sheet"
      >
        {/* Handle bar */}
        <div className="w-10 h-1 bg-zinc-200 rounded-full mx-auto mb-4" />
        
        <SheetHeader className="text-left mb-6">
          <SheetTitle className="text-lg font-semibold text-zinc-900">
            {sheetTitle}
          </SheetTitle>
          {!shareUnavailable && (
            <p className="text-sm text-zinc-500" data-testid="share-visibility">
              {isPrivate ? 'Private game · share only with intended players' : 'Public game'}
            </p>
          )}
        </SheetHeader>
        
        {shareUnavailable ? (
          <div className="mb-6 rounded-2xl border border-zinc-200 bg-zinc-50 p-5 text-center" data-testid="share-unavailable">
            <p className="font-semibold text-zinc-900">
              {lifecycle === RUN_LIFECYCLE.CANCELLED ? 'This game was canceled' : 'This game is no longer available'}
            </p>
            <p className="mt-1 text-sm text-zinc-600">
              Active-game sharing options are unavailable.
            </p>
          </div>
        ) : (
          <>
            <div className="mb-6 rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm">
              <div className="mb-3 flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                  Game summary
                </p>
              </div>
              <p className="whitespace-pre-line text-sm leading-6 text-zinc-800">
                {shareMessage}
              </p>
            </div>

            <p className="mb-4 rounded-2xl bg-orange-50 p-4 text-sm leading-6 text-orange-950">
              Share the canonical game page in messages, group chats, social posts, or printed materials.
            </p>
          </>
        )}
        
        <div className="space-y-3">
          <Button
            onClick={handleSendClickableInvite}
            disabled={clickableShareBusy || shareUnavailable}
            className="w-full h-14 rounded-xl text-base font-semibold bg-[#F97316] hover:bg-[#EA580C] text-white gap-2"
            data-testid="send-clickable-invite-btn"
          >
            <Share2 className={`w-5 h-5 ${clickableShareBusy ? 'animate-pulse' : ''}`} />
            Share Game
          </Button>

          <Button
            onClick={handleShowQrCode}
            variant="outline"
            className="w-full h-14 rounded-xl text-base font-semibold gap-2"
            disabled={qrLoading || shareUnavailable}
            data-testid="show-qr-code-btn"
          >
            <QrCode className={`w-5 h-5 ${qrLoading ? 'animate-pulse' : ''}`} />
            {qrVisible ? 'Hide QR Code' : 'Show QR Code'}
          </Button>

          {qrVisible && qrShareUrl && (
            <div
              className="rounded-2xl border border-zinc-200 bg-white p-5 text-center"
              data-testid="qr-code-panel"
            >
              <div
                ref={qrCodeContainerRef}
                className="mx-auto flex w-fit max-w-full rounded-xl bg-white p-3"
                data-testid="qr-code"
                data-qr-value={qrShareUrl}
              >
                <QRCodeSVG
                  value={qrShareUrl}
                  size={208}
                  marginSize={2}
                  title={`QR code for ${runData.title || 'OpenRuns game'}`}
                />
              </div>
              <p className="mt-3 text-sm text-zinc-600">
                Scan to open this game on OpenRuns.
              </p>
              <Button
                type="button"
                variant="outline"
                onClick={handleDownloadQrCode}
                className="mt-3 h-11 w-full rounded-xl gap-2"
                data-testid="download-qr-code-btn"
              >
                <Download className="h-4 w-4" />
                Download QR Code
              </Button>
            </div>
          )}

          <Button
            onClick={handleInstagramShare}
            variant="outline"
            className="w-full h-14 rounded-xl text-base font-semibold gap-2"
            disabled={instagramLoading || shareUnavailable}
            data-testid="share-image-btn"
          >
            <Image className={`w-5 h-5 ${instagramLoading ? 'animate-pulse' : ''}`} />
            Share Story Image + Copy Link
          </Button>

          <Button
            onClick={handleCopy}
            variant="outline"
            className="w-full h-14 rounded-xl text-base font-semibold gap-2"
            disabled={shareUnavailable}
            data-testid="copy-invite-btn"
          >
            {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
            Copy Game Summary
          </Button>

          <Button
            onClick={handleCopyLink}
            variant="outline"
            className="w-full h-14 rounded-xl text-base font-semibold gap-2"
            disabled={shareUnavailable}
            data-testid="copy-join-link-btn"
          >
            {linkCopied ? (
              <>
                <Check className="w-5 h-5 text-green-500" />
                Copied
              </>
            ) : (
              <>
                <Copy className="w-5 h-5" />
                Copy Join Link
              </>
            )}
          </Button>
        </div>
      </SheetContent>
      <InstagramShareSheet
        open={instagramOpen}
        onOpenChange={setInstagramOpen}
        {...getInstagramData()}
        onTrack={trackEvent}
      />
    </Sheet>
  );
};

export default ShareRunSheet;
